import React, {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Typography} from "@mui/material";
import Stack from '@mui/material/Stack';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import {useLocation, useNavigate} from "react-router-dom";
import Bottom from "../Bottom";
import Top from "../Top";
import HistoryDeviceBloodDetail from "./HistoryDeviceBloodDetail";
import HistoryDevicePressureDetail from "./HistoryDevicePressureDetail";
import HistoryDeviceEcgDetail from "./HistoryDeviceEcgDetail";
import GaugeChart from "../../components/GaugeChart";
import RadialChart from "../../components/RadialChart";
import LineChart from "../../components/LineChart";
import MetricBarList from "../../components/MetricBarList";
import DeviceNormalRange from "../../components/DeviceNormalRange";
import pressureIcon from "../../assets/icon/pressure.gif";
import weightIcon from "../../assets/icon/historyweight.gif";
import heartbeatIcon from "../../assets/icon/heartbeat.gif";
import bloodIcon from "../../assets/icon/blood.gif";
import * as util from "../../utils/util";
import {
  DeviceType,
  getDeviceById,
  getDeviceMetricGroups,
  getGaugeValue,
  getMetricStatus,
  getMetricStatusText,
  getPrimaryMetric,
  getStatusMetric,
  getSummaryValue
} from "../../utils/deviceInfo";
import { latestOf, trendOf, useHealthDevice } from "../../utils/healthDeviceApi";

/** 추이 그래프에 보여줄 일수 — 혈압/ECG 상세 화면과 같다 */
const TREND_DAY_COUNT = 7;

const DEVICE_ICON: { [key in DeviceType]: string } = {
  pressure: pressureIcon,
  weight: weightIcon,
  stethoscope: heartbeatIcon,
  blood: bloodIcon,
  ecg: heartbeatIcon,
};

const HistoryDeviceDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [dayValue, setDayValue] = useState(0);
  const deviceId = (location.state || {}).deviceId || '';
  const device = getDeviceById(deviceId);
  // 훅은 아래 조기 반환보다 먼저 불러야 한다(조건부 호출 금지).
  // 혈압/혈당/ECG 로 넘기는 경우 그쪽에서 각자 조회하지만, 같은 날짜는 모듈 캐시가 합쳐 준다.
  const { data: deviceData } = useHealthDevice(dayValue);

  // 선택한 기기 정보가 없으면 기기 선택 화면으로 되돌림
  if (device === undefined) {
    return (
      <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
        <Top title="기기 기록" />
        <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
          <Typography sx={{ fontSize: 16, color: '#555', textAlign: 'center', mt: 5 }}>기기 정보를 찾을 수 없습니다.</Typography>
          <Stack alignItems={'center'} sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={() => navigate('../history/HistoryDevice')} sx={DeviceChangeBtnStyle}>기기 선택하기</Button>
          </Stack>
        </Grid>
        <Bottom />
      </Stack>
    );
  }

  // 혈당기는 기존 혈당 기록 화면과 동일한 시간대별 구성 사용
  if (device.type === 'blood') {
    return <HistoryDeviceBloodDetail device={device} />;
  }

  // 혈압계는 기존 혈압 기록 화면과 동일한 수축기 / 이완기 구성 사용
  if (device.type === 'pressure') {
    return <HistoryDevicePressureDetail device={device} />;
  }

  // ECG 는 심박 기록 목록에 스트레스 지수 / 부정맥 기록을 더한 구성 사용
  if (device.type === 'ecg') {
    return <HistoryDeviceEcgDetail device={device} />;
  }

  const targetDate = util.getDateByCustom('yyyyMMdd', dayValue);
  const metricGroups = getDeviceMetricGroups(device.id);

  // AP_009_004 응답에서 그날 값과 최근 추이를 꺼낸다
  const latest = latestOf(deviceData, device, dayValue);
  const primary = getPrimaryMetric(device.id);
  const trend = trendOf(deviceData, device, TREND_DAY_COUNT, dayValue);

  const summary = getSummaryValue(device.id, latest);

  // 상태 판정 항목 (체중계는 BMI 기준)
  const statusMetric = getStatusMetric(device.id);
  const status = getMetricStatus(statusMetric, latest[statusMetric.key]);
  const gaugeValue = getGaugeValue(primary, latest[primary.key]);

  // 최근 7일 (오늘 포함) 날짜 라벨
  const trendLabels = trend.map((_, index) => {
    const date = util.getDateByCustom('yyyyMMdd', dayValue + index - (trend.length - 1));
    return date.slice(4, 6) + '.' + date.slice(6, 8);
  });

  const series = [{ curve: 'linear', data: trend }];
  const xAxis = [{ scaleType: 'point', data: trendLabels }];

  return (
    <Stack sx={{ width: '100%', minWidth: 320, background: '#F1F2F4', pb: 12, pl: 2, pr: 2, boxSizing: 'border-box' }} spacing={6}>
      <Top title={device.typeName + ' 기록'} />

      <Grid sx={{ bgcolor: '#F1F2F4', p: 0, width: '100%', minHeight: 500 }}>
        <Box>

          <Typography component="div" sx={{ fontSize: 20, color: '#555', m: 4, mb: 0, textAlign: 'center', fontWeight: 700 }}>
            <ArrowLeftIcon onClick={() => setDayValue((prev: any) => prev - 1)} sx={{ verticalAlign: 'text-bottom' }} />
            {targetDate.slice(0, 4) + '.' + targetDate.slice(4, 6) + '.' + targetDate.slice(6, 8)}
            <ArrowRightIcon onClick={() => setDayValue((prev: any) => prev + 1)} sx={{ 'display': dayValue < 0 ? 'inline-block' : 'none', verticalAlign: 'text-bottom' }} />
          </Typography>

          {/* 요약 */}
          <Box sx={{ m: '20px 0', bgcolor: '#fff', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} sx={{ p: '10px 15px 8px 15px' }}>
              <Typography sx={{ fontSize: 14, color: '#333', fontWeight: 700 }}>{device.name}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: device.excluded ? '#bbb' : '#59b544', border: '1px solid', borderColor: device.excluded ? '#e0e0e0' : '#a5dd97', borderRadius: '10px', p: '2px 8px' }}>
                {device.excluded ? '실증 제외' : '연동됨'}
              </Typography>
            </Stack>
            <Divider />

            <Stack direction={'row'} justifyContent={'center'} sx={{ p: 1 }}>
              <Grid>
                {/* 체중계는 체중 기록 화면과 동일한 방사형 게이지 사용 */}
                {device.type === 'weight' ? (
                  <RadialChart value={latest.weight_kg} />
                ) : (
                  <GaugeChart targetValue={gaugeValue} />
                )}
                <Typography component={'div'} sx={{ width: '100%', textAlign: 'center', color: status.color, fontSize: 16, fontWeight: 700, mb: 1 }}>
                  {getMetricStatusText(statusMetric, status)}
                </Typography>
              </Grid>

              <Stack justifyContent={'center'} sx={{ m: 'auto' }}>
                <Avatar alt="" src={DEVICE_ICON[device.type]} sx={{ m: 'auto', width: '60px', height: '60px', borderRadius: 0 }} />
              </Stack>

              <Stack direction={'row'} alignItems={'center'} sx={{ ml: 'auto', mr: 2 }}>
                <Stack direction={'column'} sx={{ ml: 'auto' }}>
                  <Typography component="div" sx={{ fontSize: 30, color: status.color, fontWeight: 700, textAlign: 'center' }}>
                    {summary.text}
                  </Typography>
                  <Typography component="span" sx={{ fontSize: 18, color: '#777', mb: 0, textAlign: 'center' }}>
                    {summary.unit}
                  </Typography>
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* 항목 그룹별 측정 기록 */}
          {metricGroups.map((metricGroup) => (
            <Accordion key={metricGroup.group} defaultExpanded sx={{ borderRadius: '15px', position: 'static', mb: '20px', '&:before': { display: 'none' } }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={metricGroup.group + '-content'}
                id={metricGroup.group + '-header'}
                sx={{
                  [`&#${metricGroup.group}-header`]: { minHeight: '55px' },
                  '& .MuiAccordionSummary-content.Mui-expanded': { m: 0 }
                }}
              >
                <Typography sx={{ fontSize: 16, color: '#555', fontWeight: 700 }}>{metricGroup.groupName} 기록</Typography>
              </AccordionSummary>
              <Divider />

              <AccordionDetails>
                {/* 측정값과 함께 최소~최대 범위에서의 위치 / 표준 범위를 가로 바로 표시 */}
                <MetricBarList metrics={metricGroup.list} values={latest} />
              </AccordionDetails>
            </Accordion>
          ))}

          {/* 최근 7일 추이 */}
          <Box sx={{ bgcolor: '#fff', p: '0 10px 10px 10px', borderRadius: '15px', boxShadow: CardShadow }}>
            <Stack direction={'row'} justifyContent={'center'} alignItems={'center'} sx={{ p: 1 }}>
              <Stack>
                <Typography component="div" sx={{ fontSize: 14, color: '#555' }}>최근 7일 {primary.label}</Typography>
              </Stack>
              <Stack sx={{ ml: 'auto', zIndex: 2 }}>
                <Button sx={{ fontSize: 12, color: '#555' }} endIcon={<ArrowRightIcon />}
                  onClick={() => { navigate('../history/HistoryDeviceMonthHistory', { state: { deviceId: device.id } }) }}>
                  월간현황
                </Button>
              </Stack>
            </Stack>
            <Divider />

            <Box sx={{ mt: -3.5 }}>
              {/* 체중계는 체중 기록 화면과 동일하게 정상 체중 기준선 노출 */}
              <LineChart series={series} xAxis={xAxis} isReferenceLine={device.type === 'weight'} />
            </Box>
          </Box>

          <DeviceNormalRange deviceType={device.type} />
        </Box>
      </Grid>

      <Bottom />
    </Stack>
  );
}

const CardShadow = '0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)';

const DeviceChangeBtnStyle = {
  width: '100%',
  bgcolor: '#fff',
  fontSize: 16,
  fontWeight: 700,
  borderRadius: '30px', p: 1,
  boxSizing: 'border-box',
  color: '#EF8322',
  border: '1px solid #EF8322',
  ':hover': {
    backgroundColor: '#fff',
    borderColor: '#EF8322',
    boxShadow: 'none',
  },
};

export default HistoryDeviceDetail;
